import { Link } from "react-router-dom";
import Helmet from "react-helmet";

import { useTranslation } from "@mywheels/common/lib/translation";
import { useGraphCMS } from "@mywheels/common/lib/useGraphCMS";

import { LinkArrow, Markdown, ScrollToTop } from "web/ui";
import { useLangPrefixify, useRoutes } from "web/lib/i18n";

import { BlogToolbar } from "./BlogToolbar";
import { PostMeta } from "./PostMeta";
import { _blogPicture } from "./styles";
import { BlogCard } from "./BlogCard";
import { BlogHero } from "./BlogHero";
import {
  BlogPostSearchResult,
  blogPostUrl,
  GetRecentBlogPostsQuery,
} from "./data";

export default function BlogHomePage() {
  const { t } = useTranslation("page:BlogHome");
  const routes = useRoutes();

  const { data: recentBlogPosts, isFetching } = useGraphCMS(
    GetRecentBlogPostsQuery,
    {}
  );

  return (
    <>
      <Helmet>
        <title>{t("Blog")}</title>
        <link href={routes.Blog.url()} rel="canonical" />
      </Helmet>

      <ScrollToTop reactToSearchChange>
        <BlogHero className="mb-10">
          <div className="absolute inset-0 bg-gray900 bg-opacity-20" />
          <div className="relative container py-5 md:py-13 text-white">
            <div className="row">
              <div className="col-12 md:col-7">
                <h1 className="text-white md:text-5xl mb-5">
                  {t("MyWheels Blog")}
                </h1>
                <p className="text-xl">
                  {t(
                    "Bekijk het laatste nieuws over MyWheels, autodelen en onze weg naar minder auto's op straat."
                  )}
                </p>
              </div>
            </div>
          </div>
          <div
            // compensate for the diagonal cut-off
            style={{ height: "4rem" }}
          />
        </BlogHero>

        <div className="container mb-12">
          {recentBlogPosts?.[0] && <BlogFeature post={recentBlogPosts[0]} />}
          <BlogToolbar loading={isFetching} />
          <div className="row mb-3">
            {recentBlogPosts?.slice(1).map((post) => (
              <div key={post.id} className="col-12 md:col-4">
                <BlogCard post={post} />
              </div>
            ))}
          </div>
          <div className="row mb-5 flex justify-center">
            <Link
              className="btn btn-blue"
              to={routes.BlogArchive.url({
                filter: "alles",
              })}
            >
              {t("Zie alle berichten")}
              <LinkArrow />
            </Link>
          </div>
        </div>
      </ScrollToTop>
    </>
  );
}

function BlogFeature({ post }: { post: BlogPostSearchResult }) {
  const { t } = useTranslation("page:BlogHome");
  const lp = useLangPrefixify();

  return (
    <div className="row mb-12">
      {post.featuredImage && (
        <div className="col-12 md:col-6">
          <Link
            className={_blogPicture}
            style={{
              backgroundImage: `url("https://media.graphcms.com/output=format:jpg/resize=,height:800,fit:max/${post.featuredImage.handle}")`,
            }}
            to={lp(blogPostUrl(post))}
          />
        </div>
      )}
      <div className="col-12 md:col-6 mt-2">
        <Link
          to={lp(blogPostUrl(post))}
          style={{ textDecoration: "none", color: "#222" }}
        >
          <h2 className="text-4xl">{post.title}</h2>
        </Link>
        <PostMeta post={post} />
        <div className="text-muted mt-3">
          <Markdown
            source={post.excerpt || ""}
            inline={false}
            className="prose"
          />
        </div>
        <Link
          className="font-display text-gray underline hover:text-gray700"
          to={lp(blogPostUrl(post))}
        >
          {t("Lees meer")}
          <LinkArrow />
        </Link>
      </div>
    </div>
  );
}
